<template>
  <div class="annotation-list">
    <!-- 筛选卡片 -->
    <el-card shadow="never" class="filter-card">
      <el-form :model="filters" size="small" class="filter-form">
        <el-form-item>
          <el-input v-model="filters.vin" placeholder="车架VIN" clearable />
        </el-form-item>
        <el-form-item>
          <el-select v-model="filters.status" placeholder="标注状态" clearable>
            <el-option label="处理中" value="processing" />
            <el-option label="已完成" value="completed" />
            <el-option label="等待中" value="pending" />
            <el-option label="失败" value="failed" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-input v-model="filters.object_name" placeholder="文件夹名称" clearable />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 标签筛选区域 -->
      <div class="tag-filter-section">
        <div class="tag-filter-toggle" @click="toggleTagFilter">
          <i :class="showTagFilter ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
          <span>{{ showTagFilter ? '收起标签筛选' : '展开标签筛选' }}</span>
          <span v-if="activeTagCount > 0" class="tag-filter-badge">{{ activeTagCount }} 项条件</span>
        </div>
        <div v-show="showTagFilter" class="tag-filter-grid">
          <div v-for="group in tagFilterGroups" :key="group.key" class="tag-filter-item">
            <label class="tag-filter-label">{{ group.label }}</label>
            <el-select
              v-if="group.selection === 'single'"
              v-model="tagFilters[group.key]"
              :placeholder="`选择${group.label}`"
              clearable
              size="small"
              @change="handleTagChange"
            >
              <el-option
                v-for="item in group.items"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
            <el-select
              v-else
              v-model="tagFilters[group.key]"
              :placeholder="`选择${group.label}`"
              multiple
              clearable
              size="small"
              collapse-tags
              @change="handleTagChange"
            >
              <el-option
                v-for="item in group.items"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 分组表格卡片 -->
    <el-card shadow="never" class="table-card">
      <div slot="header" class="table-header">
        <span>标注分组</span>
        <span class="total-count">共 {{ total }} 组</span>
      </div>

      <el-table
        v-loading="loading"
        :data="tableData"
        ref="annotationTable"
        height="100%"
        stripe
        border
        size="small"
        style="width:100%"
        :default-sort="{ prop: 'latest_task_at', order: 'descending' }"
        @expand-change="handleExpand"
      >
        <el-table-column type="expand">
          <template slot-scope="{ row }">
            <div class="expand-content" v-if="row.label_count > 0">
              <div class="label-section">
                <h4 class="section-title">标签分布</h4>
                <el-table
                  :data="flattenDist(row)"
                  size="mini"
                  border
                  class="dist-table"
                  max-height="320"
                >
                  <el-table-column prop="category" label="分类" width="80" />
                  <el-table-column label="标签值" min-width="140">
                    <template slot-scope="{ row: r }">
                      <div class="label-tags">
                        <el-tag
                          v-for="(item, idx) in r.labels"
                          :key="idx"
                          :type="getFieldTagType(r.fieldKey)"
                          size="mini"
                        >{{ item.label }}:{{ item.count }}</el-tag>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </div>
            <div class="expand-content" v-else>
              <span class="no-label-hint">暂无标注数据</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="关键帧" width="110" align="center">
          <template slot-scope="{ row }">
            <KeyframeThumbnail
              :first-keyframe-id="row.first_keyframe_id"
              :object-name="row.object_name"
              :record-mode="row.record_mode"
            />
          </template>
        </el-table-column>
        <el-table-column prop="object_name" label="文件夹" min-width="220" show-overflow-tooltip />
        <el-table-column label="PB文件数" width="90" align="center" prop="pb_file_count" />
        <el-table-column prop="vin" label="车架VIN" width="200" show-overflow-tooltip />
        <el-table-column label="标注状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="row.status === 'completed'" type="success" size="mini">已标注</el-tag>
            <el-tag v-else-if="row.status === 'processing'" size="mini" color="#409EFF" style="color:#fff;border-color:#409EFF">识别中</el-tag>
            <el-tag v-else-if="row.status === 'failed'" type="danger" size="mini">失败</el-tag>
            <el-tag v-else-if="row.status === 'pending'" type="info" size="mini">等待中</el-tag>
            <span v-else class="no-status">-</span>
          </template>
        </el-table-column>
        <el-table-column label="进度" width="140" align="center">
          <template slot-scope="{ row }">
            <span v-if="row.status === 'completed'" class="progress-done">100%</span>
            <el-progress
              v-else-if="row.total_frames > 0"
              :percentage="Math.round((row.processed_frames / row.total_frames) * 100)"
              :status="row.status === 'failed' ? 'exception' : undefined"
              :stroke-width="7"
            />
            <span v-else class="no-progress">-</span>
          </template>
        </el-table-column>
        <el-table-column label="标签数" width="80" align="center" prop="label_count">
          <template slot-scope="{ row }">
            <span class="label-count">{{ row.label_count }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="record_date" label="记录日期" width="100" align="center" sortable />
        <el-table-column prop="record_time" label="记录时间" width="90" align="center" />
        <el-table-column label="操作" width="100" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              type="text"
              size="mini"
              icon="el-icon-refresh"
              :loading="row._reannotating"
              @click.stop="handleReannotate(row)"
            >
              重新标注
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          background
          :current-page="pagination.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import { fetchAnnotationGroups, fetchLabelDictionary, triggerReannotate } from '../api/label';
import KeyframeThumbnail from '../components/KeyframeThumbnail.vue';

export default {
  name: 'AnnotationList',

  components: { KeyframeThumbnail },

  data() {
    return {
      loading: false,
      tableData: [],
      total: 0,
      showTagFilter: false,
      tagGroups: {},       // 标签字典: { scene: { label, selection, items }, ... }
      tagFilters: {        // 当前标签筛选值
        scene: '',
        weather: '',
        time: '',
        lighting: '',
        road: '',
        state: '',
        dynamic: [],
        static: [],
        anomaly: [],
      },
      pagination: {
        page: 1,
        pageSize: 20,
      },
      filters: {
        vin: '',
        status: '',
        object_name: '',
      },
      distFields: [
        { key: 'scene', label: '场景', tagType: '' },
        { key: 'weather', label: '天气', tagType: 'success' },
        { key: 'time', label: '时段', tagType: 'warning' },
        { key: 'lighting', label: '光照', tagType: 'info' },
        { key: 'road', label: '道路', tagType: 'danger' },
        { key: 'state', label: '状态', tagType: 'success' },
        { key: 'dynamic', label: '动态元素', tagType: 'warning' },
        { key: 'static', label: '静态元素', tagType: '' },
        { key: 'anomaly', label: '异常情况', tagType: 'danger' },
      ],
    };
  },

  computed: {
    /** 标签筛选分组列表（按字典顺序） */
    tagFilterGroups() {
      const order = ['scene', 'weather', 'time', 'lighting', 'road', 'state', 'dynamic', 'static', 'anomaly'];
      return order
        .filter(key => this.tagGroups[key])
        .map(key => ({ key, ...this.tagGroups[key] }));
    },

    activeTagCount() {
      let count = 0;
      for (const [key, val] of Object.entries(this.tagFilters)) {
        if (Array.isArray(val) && val.length > 0) count++;
        else if (!Array.isArray(val) && val !== '' && val !== null) count++;
      }
      return count;
    },
  },

  mounted() {
    this.loadDictionary();
    this.loadData();
  },

  methods: {
    async loadDictionary() {
      try {
        const res = await fetchLabelDictionary();
        this.tagGroups = res.data.groups;
      } catch {
        // 字典加载失败不影响主体功能
      }
    },

    async loadData() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
        };

        if (this.filters.vin) params.vin = this.filters.vin;
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.object_name) params.object_name = this.filters.object_name;

        for (const [key, val] of Object.entries(this.tagFilters)) {
          if (Array.isArray(val) && val.length > 0) {
            params[`tag_${key}`] = val.join(',');
          } else if (!Array.isArray(val) && val !== '' && val !== null) {
            params[`tag_${key}`] = val;
          }
        }

        const res = await fetchAnnotationGroups(params);
        this.tableData = res.data.rows;
        this.total = res.data.total;
      } catch {
        this.tableData = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    handleSearch() {
      this.pagination.page = 1;
      this.loadData();
    },

    handleTagChange() {
      this.handleSearch();
    },

    /** 展开/收起标签筛选会顶掉表格高度，显式让表格重算内部滚动区 */
    toggleTagFilter() {
      this.showTagFilter = !this.showTagFilter;
      this.$nextTick(() => {
        const table = this.$refs.annotationTable;
        if (table && table.doLayout) table.doLayout();
      });
    },

    handleReset() {
      this.filters = {
        vin: '',
        status: '',
        object_name: '',
      };
      this.tagFilters = {
        scene: '',
        weather: '',
        time: '',
        lighting: '',
        road: '',
        state: '',
        dynamic: [],
        static: [],
        anomaly: [],
      };
      this.pagination.page = 1;
      this.loadData();
    },

    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.pagination.page = 1;
      this.loadData();
    },

    handlePageChange(val) {
      this.pagination.page = val;
      this.loadData();
    },

    handleExpand(_row, _expandedRows) {
    },

    handleReannotate(row) {
      this.$confirm(
        `确认要重新标注文件夹「${row.object_name}」吗？<br/>此操作将清除现有标注数据并重新执行 AI 标注。`,
        '确认重新标注',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
          dangerouslyUseHTMLString: true,
        },
      )
        .then(async () => {
          this.$set(row, '_reannotating', true);
          try {
            const res = await triggerReannotate(row.object_name);
            if (res.code === 0) {
              const msg = res.data && res.data.queued
                ? '标注任务已重置，将在当前任务完成后自动开始。'
                : '重新标注已开始。';
              this.$message.success(msg);
            } else {
              this.$message.error(res.message || '触发重新标注失败');
            }
          } catch {
            this.$message.error('触发重新标注失败，请检查网络连接。');
          } finally {
            this.$set(row, '_reannotating', false);
            this.loadData();
          }
        })
        .catch(() => {
        });
    },

    flattenDist(row) {
      if (!row.label_distribution) return [];
      const rows = [];
      this.distFields.forEach((field) => {
        const dist = row.label_distribution[field.key];
        if (!dist || Object.keys(dist).length === 0) return;
        const entries = Object.entries(dist)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 8);
        if (entries.length === 0) return;
        rows.push({
          fieldKey: field.key,
          category: field.label,
          labels: entries.map(([label, count]) => ({ label, count })),
        });
      });
      return rows;
    },

    getFieldTagType(fieldKey) {
      const field = this.distFields.find((f) => f.key === fieldKey);
      return field ? field.tagType : '';
    },
  },
};
</script>

<style scoped>
/* 页面撑满 main 区：顶栏与筛选卡固定，剩余高度全给表格卡 */
.annotation-list {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.filter-card {
  flex: 0 0 auto;
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid #e8eaed;
}

.filter-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.filter-form {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 4px 16px;
}

.filter-form .el-form-item {
  margin-bottom: 0;
}

.filter-form .el-form-item .el-input,
.filter-form .el-form-item .el-select {
  width: 100%;
}

.table-card {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid #e8eaed;
}

.table-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

/* 卡片内容区接管剩余高度：表格自适应，分页始终贴底 */
.table-card ::v-deep .el-card__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.table-card ::v-deep .el-card__body > .el-table {
  flex: 1 1 0;
  min-height: 0;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
}

.total-count {
  color: #909399;
  font-size: 13px;
  font-weight: 400;
}

.pagination-wrap {
  flex: 0 0 auto;
  margin-top: 12px;
  text-align: right;
}

/* 统计 */
.frame-stat {
  color: #606266;
  font-size: 13px;
}

.label-count {
  font-weight: 600;
  color: #409EFF;
}

.no-status {
  color: #c0c4cc;
}

.no-progress {
  color: #c0c4cc;
}

.progress-done {
  color: #67c23a;
  font-weight: 600;
  font-size: 13px;
}

/* 展开行样式 */
.expand-content {
  padding: 8px 16px 16px;
}

.section-title {
  margin: 0 0 12px 0;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.dist-table {
  width: 100%;
}

.dist-count {
  font-weight: 600;
  color: #409EFF;
  font-size: 13px;
}

.label-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 6px;
  align-items: center;
}

.no-label-hint {
  font-size: 13px;
  color: #909399;
}

/* 标签筛选区域 */
.tag-filter-section {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
}

.tag-filter-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #409eff;
  cursor: pointer;
  user-select: none;
}

.tag-filter-toggle:hover {
  color: #66b1ff;
}

.tag-filter-badge {
  color: #e6a23c;
  font-weight: 500;
}

.tag-filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px 16px;
  margin-top: 12px;
}

.tag-filter-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tag-filter-label {
  font-size: 12px;
  color: #909399;
  line-height: 1;
}

.tag-filter-item .el-select {
  width: 100%;
}
</style>
